import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import axios from '../utils/axios';
import { translations } from '../translations';
import { adminTranslations } from '../translations/admin';
import { commonTranslations } from '../translations/common';
import { cashierTranslations } from '../translations/cashier';

export const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ar');
  const [initialized, setInitialized] = useState(false);
  const isFirstMount = useRef(true);

  // Handle initial setup
  useEffect(() => {
    const loadDefaultLanguage = async () => {
      if (!localStorage.getItem('language')) {
        try {
          const response = await axios.get('/settings');
          if (response.data?.interface?.default_language) {
            const defaultLang = response.data.interface.default_language;
            setLanguage(defaultLang);
            localStorage.setItem('language', defaultLang);
          }
        } catch (error) {
          console.error('Error loading default language:', error);
        }
      }
      setInitialized(true);
    };

    if (isFirstMount.current) {
      loadDefaultLanguage();
      isFirstMount.current = false;
    }
  }, []);

  // Handle document attributes
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    const newLanguage = language === 'ar' ? 'en' : 'ar';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  }, [language]);

  const getNestedValue = (obj, path) => {
    return path.reduce((current, key) => {
      return current?.[key];
    }, obj);
  };

  const t = useCallback((key) => {
    if (!key) return '';

    // Handle namespaced keys (e.g., 'admin:categories.create')
    const [namespace, ...rest] = key.split(':');
    const translationKey = rest.length ? rest.join(':') : namespace;
    const keys = translationKey.split('.');

    let translationObj;
    if (rest.length) {
      // Namespaced translation
      switch (namespace) {
        case 'admin':
          translationObj = adminTranslations[language];
          break;
        case 'common':
          translationObj = commonTranslations[language].common;
          break;
        case 'cashier':
          translationObj = cashierTranslations[language];
          break;
        default:
          translationObj = translations[language];
      }
    } else {
      // Non-namespaced translation - first try main translations
      translationObj = translations[language];
      const value = getNestedValue(translationObj, keys);
      if (value !== undefined) return value;

      // Then try common translations
      translationObj = commonTranslations[language].common;
    }

    const value = getNestedValue(translationObj, keys);
    
    if (value === undefined) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    return value;
  }, [language]);

  const isRTL = language === 'ar';

  const value = {
    language,
    setLanguage: (newLanguage) => {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
    },
    toggleLanguage,
    t,
    isRTL,
    initialized
  };

  if (!initialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
